/**
 * Platform-admin runtime GIT browser (RuntimeOverview "Case History
 * (Git)" tab) — read-only commit log, commit detail, and an assembled
 * side-by-side diff for a case workspace's `.git` repo.
 *
 * This is the operator-facing twin of the agent's `lx_case_history`, and
 * it is deliberately DISTINCT from its two neighbours:
 *   - `/checkpoints` (Checkpoints.tsx) = executionLedger metadata about
 *     checkpoint RUNS (did they fire, did they fail). No repo access.
 *   - "Workspace" tab (RuntimeFsBrowser) = the WORKING TREE as it is
 *     right now. No history, no versions, no diffs.
 * This tab is the repository itself: what changed, when, and by which
 * checkpoint trigger.
 *
 * REST-based (NOT useGatewayRpc), same shape as the Workspace tab: it
 * goes through lexserver's `/api/platform/runtimes/:userId/git/*`
 * passthrough proxy (platform.admin-gated), keyed on the selected
 * runtime's userId from useRuntimeContext. Client + frozen contract:
 * [git-browser-api.ts](../lib/git-browser-api.ts).
 *
 * ⚠ There is no unified diff in the API (the baked `lx-git` verb blocks
 * `-p`/`-U`), so a file's diff is assembled from TWO blob reads and
 * aligned client-side — see [git-diff.ts](../lib/git-diff.ts).
 *
 * Handoff thread: `…-operator-facing-git-re`; backend pieces are
 * lexserver `aff0c5e92` (proxy) + lexiun-core `5fd262b3d` (git reads).
 */

import { FileDiff, GitCommit, History, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { useRuntimeContext } from "@/hooks/useRuntimeContext";
import {
  classifyTrigger,
  describeGitError,
  getGitBlob,
  getGitCommit,
  getGitLog,
  GitBrowserError,
  type GitCommitDetail,
  type GitCommitFile,
  type GitCommitRow,
} from "@/lib/git-browser-api";
import { alignDiff, planBlobReads, splitLines, type DiffResult } from "@/lib/git-diff";
import type { CaseRegistryEntry } from "@/types/platform";

const LOG_PAGE_SIZE = 50;

/** Diff pane state for the currently-open file. `binary` short-circuits
 *  the whole view (the API never ships bytes — see the client's note). */
interface FileDiffState {
  file: GitCommitFile;
  diff: DiffResult | null;
  binary: boolean;
  truncated: boolean;
}

export function RuntimeGitBrowser() {
  const api = usePlatformApi();
  const { target } = useRuntimeContext();
  const userId = target?.userId ?? null;
  const orgId = target?.orgId ?? null;

  const [cases, setCases] = useState<CaseRegistryEntry[]>([]);
  const [caseId, setCaseId] = useState<string>("");
  const [casesError, setCasesError] = useState<string | null>(null);

  const [commits, setCommits] = useState<GitCommitRow[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loadingLog, setLoadingLog] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);

  const [selectedSha, setSelectedSha] = useState<string | null>(null);
  const [detail, setDetail] = useState<GitCommitDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [fileDiff, setFileDiff] = useState<FileDiffState | null>(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);

  // Monotonic request tokens. The two async CALLBACKS below (`loadLog`,
  // `openFile`) are user-triggered and can overlap — click file A then
  // file B and A's slower response lands LAST, painting B's row as
  // selected while showing A's diff. The useEffects guard this with a
  // `cancelled` closure flag, but a callback has no cleanup hook, so it
  // needs a ref: only the newest token may commit its result.
  const logSeqRef = useRef(0);
  const diffSeqRef = useRef(0);

  // Case list for the picker — scoped to the connected runtime's org.
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    api
      .listCases(orgId)
      .then((res) => {
        if (cancelled) return;
        setCases(res.data);
        setCasesError(null);
      })
      .catch((e) => {
        if (!cancelled) {
          setCasesError(e instanceof Error ? e.message : "Failed to list cases");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, orgId]);

  // Changing the case resets everything downstream of it.
  useEffect(() => {
    setCommits([]);
    setCursor(null);
    setSelectedSha(null);
    setDetail(null);
    setFileDiff(null);
    setDiffError(null);
    setDetailError(null);
  }, [caseId]);

  const loadLog = useCallback(
    async (append: boolean, appendCursor?: number) => {
      if (!userId || !caseId) return;
      const seq = ++logSeqRef.current;
      setLoadingLog(true);
      try {
        const res = await getGitLog(userId, caseId, {
          limit: LOG_PAGE_SIZE,
          cursor: append ? appendCursor : undefined,
        });
        // Superseded (the operator switched case / paged again) — drop it,
        // or a previous case's commits would land under the new selection.
        if (seq !== logSeqRef.current) return;
        setCommits((prev) => (append ? [...prev, ...res.commits] : res.commits));
        setCursor(res.nextCursor);
        setLogError(null);
      } catch (e) {
        if (seq !== logSeqRef.current) return;
        setLogError(
          e instanceof GitBrowserError
            ? describeGitError(e)
            : e instanceof Error
              ? e.message
              : "Failed to load commit log",
        );
      } finally {
        // Only the newest request owns the spinner.
        if (seq === logSeqRef.current) setLoadingLog(false);
      }
    },
    [userId, caseId],
  );

  useEffect(() => {
    if (userId && caseId) void loadLog(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, caseId]);

  // Commit detail (the changed-path list) for the selected commit.
  useEffect(() => {
    if (!userId || !caseId || !selectedSha) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    setDetailError(null);
    setFileDiff(null);
    setDiffError(null);
    getGitCommit(userId, caseId, selectedSha)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (cancelled) return;
        setDetail(null);
        setDetailError(
          e instanceof GitBrowserError
            ? describeGitError(e)
            : e instanceof Error
              ? e.message
              : "Failed to load commit",
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, caseId, selectedSha]);

  /**
   * Open one file's diff: plan the reads (a rename reads the BEFORE side
   * at `oldPath`; an add has no before; a delete has no after), fetch
   * both blobs in parallel, then align client-side.
   */
  const openFile = useCallback(
    async (file: GitCommitFile) => {
      if (!userId || !caseId || !detail || !selectedSha) return;
      const seq = ++diffSeqRef.current;
      setLoadingDiff(true);
      setDiffError(null);
      setFileDiff(null);
      const plan = planBlobReads(file, detail.parentSha);
      try {
        if (file.binary) {
          setFileDiff({ file, diff: null, binary: true, truncated: false });
          return;
        }
        const [parentBlob, currentBlob] = await Promise.all([
          plan.needParent && plan.parentPath
            ? getGitBlob(userId, caseId, detail.parentSha!, plan.parentPath)
            : Promise.resolve(null),
          plan.needCurrent && plan.currentPath
            ? getGitBlob(userId, caseId, selectedSha, plan.currentPath)
            : Promise.resolve(null),
        ]);
        // Two blob reads raced; a newer file click supersedes this one.
        if (seq !== diffSeqRef.current) return;
        if (parentBlob?.binary || currentBlob?.binary) {
          setFileDiff({ file, diff: null, binary: true, truncated: false });
          return;
        }
        const before = splitLines(parentBlob?.content ?? "");
        const after = splitLines(currentBlob?.content ?? "");
        setFileDiff({
          file,
          diff: alignDiff(before, after),
          binary: false,
          truncated: Boolean(parentBlob?.truncated || currentBlob?.truncated),
        });
      } catch (e) {
        if (seq !== diffSeqRef.current) return;
        setDiffError(
          e instanceof GitBrowserError
            ? describeGitError(e)
            : e instanceof Error
              ? e.message
              : "Failed to load file contents",
        );
      } finally {
        if (seq === diffSeqRef.current) setLoadingDiff(false);
      }
    },
    [userId, caseId, detail, selectedSha],
  );

  const selectedCommit = useMemo(
    () => commits.find((c) => c.sha === selectedSha) ?? null,
    [commits, selectedSha],
  );

  if (!userId) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <History className="h-8 w-8 text-slate-400" />
        <h1 className="mt-2 text-xl font-semibold">Case History (Git)</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Select a runtime from the fleet to browse a case repository.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3" data-testid="runtime-git-browser">
      {/* Case picker — a git repo exists PER CASE, so nothing loads until
          one is chosen. */}
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <label className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Case
        </label>
        <select
          value={caseId}
          onChange={(e) => setCaseId(e.target.value)}
          className="min-w-[22rem] max-w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-950"
          data-testid="git-case-select"
        >
          <option value="">Select a case…</option>
          {cases.map((c) => (
            <option key={c.caseId} value={c.caseId}>
              {c.title ? `${c.title} — ${c.caseId}` : c.caseId}
            </option>
          ))}
        </select>
        {caseId && (
          <span className="text-xs text-slate-400">
            {commits.length} commit{commits.length === 1 ? "" : "s"} loaded
          </span>
        )}
        {casesError && (
          <span className="text-xs text-rose-600 dark:text-rose-400">{casesError}</span>
        )}
      </div>

      {!caseId ? (
        <div className="flex flex-1 items-center justify-center rounded-md border border-dashed border-slate-200 text-sm text-slate-400 dark:border-slate-700">
          Choose a case to browse its checkpoint history.
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3">
          {/* ── Commit log ─────────────────────────────────────────── */}
          <Card className="flex min-h-0 flex-col overflow-hidden">
            <CardHeader className="shrink-0 py-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <GitCommit className="h-4 w-4" />
                Commits
                {loadingLog && <Loader2 className="h-3 w-3 animate-spin" />}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-auto p-0" data-testid="git-commit-log">
              {logError && (
                <div className="m-3 rounded-md bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                  {logError}
                </div>
              )}
              {!loadingLog && !logError && commits.length === 0 && (
                <div className="p-6 text-center text-sm text-slate-400">
                  No history yet — this case has no checkpoints.
                </div>
              )}
              {commits.map((c) => (
                <CommitRow
                  key={c.sha}
                  commit={c}
                  selected={selectedSha === c.sha}
                  onSelect={() => setSelectedSha(c.sha)}
                />
              ))}
              {cursor !== null && (
                <button
                  type="button"
                  onClick={() => void loadLog(true, cursor)}
                  disabled={loadingLog}
                  className="w-full border-t border-slate-200 py-2 text-sm font-medium text-indigo-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-indigo-400 dark:hover:bg-slate-800"
                  data-testid="git-log-load-more"
                >
                  {loadingLog ? "Loading…" : "Load more"}
                </button>
              )}
            </CardContent>
          </Card>

          {/* ── Commit detail + diff ───────────────────────────────── */}
          <Card className="flex min-h-0 flex-col overflow-hidden">
            <CardHeader className="shrink-0 py-2">
              <CardTitle className="flex items-center gap-2 truncate text-sm font-medium text-muted-foreground">
                <FileDiff className="h-4 w-4 shrink-0" />
                {selectedCommit ? (
                  <span className="truncate">
                    <span className="font-mono">{selectedCommit.sha7}</span>{" "}
                    {selectedCommit.subject}
                  </span>
                ) : (
                  "Commit detail"
                )}
                {(loadingDetail || loadingDiff) && (
                  <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-auto p-0" data-testid="git-commit-detail">
              {!selectedSha && (
                <div className="p-6 text-center text-sm text-slate-400">
                  Select a commit to see what it changed.
                </div>
              )}
              {detailError && (
                <div className="m-3 rounded-md bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                  {detailError}
                </div>
              )}
              {detail && (
                <div className="flex flex-col">
                  {selectedCommit?.note && (
                    <p className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs italic text-slate-600 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-300">
                      {selectedCommit.note}
                    </p>
                  )}
                  {detail.parentSha === null && (
                    <p className="border-b border-slate-100 px-3 py-1.5 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                      Root commit — every file reads as added.
                    </p>
                  )}
                  <div className="border-b border-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-500 dark:border-slate-800 dark:text-slate-400">
                    {detail.files.length} file{detail.files.length === 1 ? "" : "s"} changed
                  </div>
                  {detail.files.map((f) => (
                    <FileRow
                      key={`${f.status}:${f.path}`}
                      file={f}
                      selected={fileDiff?.file.path === f.path}
                      onSelect={() => void openFile(f)}
                    />
                  ))}
                  {diffError && (
                    <div className="m-3 rounded-md bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                      {diffError}
                    </div>
                  )}
                  {fileDiff && <DiffPane state={fileDiff} />}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── Commit row ────────────────────────────────────────────────────────

function CommitRow({
  commit,
  selected,
  onSelect,
}: {
  commit: GitCommitRow;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full flex-col gap-0.5 border-b border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800 ${
        selected ? "bg-indigo-50 dark:bg-indigo-950" : ""
      }`}
      data-testid={`git-commit-row-${commit.sha7}`}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
          {commit.sha7}
        </span>
        <span className={triggerPillCls(commit.lexTrigger)}>{commit.lexTrigger}</span>
        <span className="ml-auto shrink-0 text-xs text-slate-400">
          {new Date(commit.authorDate).toLocaleString()}
        </span>
      </div>
      <span className="truncate text-slate-700 dark:text-slate-300">{commit.subject}</span>
      <span className="text-xs text-slate-400">
        {/* `filesChanged` counts PATHS and a rename contributes both
            sides — labelled "paths" so it can't be misread as files. */}
        {commit.filesChanged} path{commit.filesChanged === 1 ? "" : "s"}
        {commit.note ? " · has note" : ""}
      </span>
    </button>
  );
}

/** `Lex-Trigger` pill. `delete` + `restore` are the recovery-narrative
 *  triggers an operator is usually hunting for, so they get the loud
 *  colours; routine runs/leftovers/sweeps stay quiet.
 *
 *  Classified via `classifyTrigger()` rather than `===`: the live wire
 *  carries qualified values (`run-terminal`, `leftover-pre-run`), so
 *  exact matching would grey out the two MOST common triggers. */
function triggerPillCls(trigger: string): string {
  const base =
    "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide";
  switch (classifyTrigger(trigger)) {
    case "delete":
      return `${base} bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200`;
    case "restore":
      return `${base} bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200`;
    case "run":
      return `${base} bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200`;
    case "sweep":
      return `${base} bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200`;
    default:
      return `${base} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300`;
  }
}

// ─── File row ──────────────────────────────────────────────────────────

function FileRow({
  file,
  selected,
  onSelect,
}: {
  file: GitCommitFile;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-2 border-b border-slate-100 px-3 py-1.5 text-left text-xs hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800 ${
        selected ? "bg-indigo-50 dark:bg-indigo-950" : ""
      }`}
      data-testid={`git-file-row-${file.path}`}
    >
      <span className={statusPillCls(file.status)}>{file.status}</span>
      <span className="truncate font-mono text-slate-700 dark:text-slate-300">
        {file.oldPath ? `${file.oldPath} → ${file.path}` : file.path}
      </span>
      <span className="ml-auto shrink-0 font-mono text-[11px]">
        {file.binary ? (
          <span className="text-slate-400">binary</span>
        ) : (
          <>
            <span className="text-emerald-600 dark:text-emerald-400">
              +{file.added ?? 0}
            </span>{" "}
            <span className="text-rose-600 dark:text-rose-400">
              −{file.removed ?? 0}
            </span>
          </>
        )}
      </span>
    </button>
  );
}

function statusPillCls(status: string): string {
  const base =
    "shrink-0 rounded px-1 py-0.5 font-mono text-[10px] font-semibold";
  if (status === "A") {
    return `${base} bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200`;
  }
  if (status === "D") {
    return `${base} bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200`;
  }
  if (status === "R" || status === "C") {
    return `${base} bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200`;
  }
  return `${base} bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200`;
}

// ─── Diff pane ─────────────────────────────────────────────────────────

function DiffPane({ state }: { state: FileDiffState }) {
  if (state.binary) {
    return (
      <div
        className="m-3 rounded-md border border-slate-200 p-4 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
        data-testid="git-diff-binary"
      >
        Binary file — not viewable. Lexiun Git tracks it, but the read API
        never ships raw bytes.
      </div>
    );
  }
  if (!state.diff) return null;
  return (
    <div className="flex flex-col" data-testid="git-diff-pane">
      {state.truncated && (
        <p className="border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          Content capped at 256 KB — this file is truncated, so the tail is
          not shown.
        </p>
      )}
      {state.diff.degraded && (
        <p className="border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          File too large to align line-by-line — rows are paired by
          position, so an inserted line shifts everything below it. Treat
          the highlighting as approximate.
        </p>
      )}
      <table className="w-full border-collapse font-mono text-[11px]">
        <tbody>
          {state.diff.rows.map((r, i) => (
            <tr key={i} className={rowCls(r.kind)}>
              <td className="w-10 select-none border-r border-slate-200 px-1 text-right align-top text-slate-400 dark:border-slate-700">
                {r.leftNo ?? ""}
              </td>
              <td className="w-1/2 whitespace-pre-wrap break-all px-2 align-top">
                {r.leftText ?? ""}
              </td>
              <td className="w-10 select-none border-r border-l border-slate-200 px-1 text-right align-top text-slate-400 dark:border-slate-700">
                {r.rightNo ?? ""}
              </td>
              <td className="w-1/2 whitespace-pre-wrap break-all px-2 align-top">
                {r.rightText ?? ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function rowCls(kind: string): string {
  if (kind === "add") return "bg-emerald-50 dark:bg-emerald-950/40";
  if (kind === "del") return "bg-rose-50 dark:bg-rose-950/40";
  if (kind === "change") return "bg-amber-50 dark:bg-amber-950/30";
  return "";
}
